/**
 * Create Teacher Page
 * Admin form to create a new teacher account
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';
import { useNotification } from '@/hooks/useNotification';

export const CreateTeacherPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    classLimit: 5,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email) {
      showNotification('error', 'Please fill in all required fields');
      return;
    }

    // Validate email domain matches school
    const emailDomain = formData.email.split('@')[1];
    const userDomain = user?.email?.split('@')[1];
    
    if (emailDomain !== userDomain) {
      showNotification('error', `Email must be from your school domain: @${userDomain}`);
      return;
    }

    try {
      setIsSubmitting(true);
      
      const response = await apiRequest('admin_create_teacher', {
        adminEmail: user?.email,
        teacherEmail: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        classLimit: formData.classLimit,
      });

      if (response.success) {
        showNotification('success', 'Teacher account created! They will receive an email with setup instructions.');
        navigate('/teachers');
      } else {
        showNotification('error', (response as any).error || 'Failed to create teacher');
      }
    } catch (error) {
      console.error('Failed to create teacher:', error);
      showNotification('error', 'Failed to create teacher');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Add Teacher</h1>
          <p className="text-slate-600 mt-2">Create a new teacher account for your school</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/teachers')}>
          Cancel
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  First Name *
                </label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Last Name *
                </label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email Address *
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={`teacher@${user?.email?.split('@')[1] || 'school.com'}`}
                required
              />
              <p className="text-sm text-slate-500 mt-2">
                Must use your school email domain: @{user?.email?.split('@')[1]}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Class Limit
              </label>
              <Input
                type="number"
                value={formData.classLimit}
                onChange={(e) => setFormData({ ...formData, classLimit: parseInt(e.target.value) || 0 })}
                min="0"
                max="50"
              />
              <p className="text-sm text-slate-500 mt-2">
                Maximum number of classes this teacher can create
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">Account Setup</p>
                  <p>The teacher will receive an email with a link to set their password and get started.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Teacher Account'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/teachers')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
};
